/**
 * HOPE Dynamic Form System
 * 
 * Implements:
 * - HOPE Admission Form (Golden Record)
 * - HOPE Update Forms with Delta Comparison
 * - Symptom Focused Visit (SFV) Auto-Trigger
 * - HOPE Discharge with Goal Validation
 * - iQIES Export Functionality
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";
import { Switch } from "./ui/switch";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Slider } from "./ui/slider";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  Lock,
  ArrowUp,
  ArrowDown,
  Minus,
  Save,
  Send,
  Download,
  AlertCircle,
  Activity,
  Heart,
  Stethoscope,
  ClipboardList,
  Calendar,
  User,
  RefreshCw,
  ChevronRight,
  Info,
  XCircle,
  Check
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

// API paths
const HOPE_API = `${API_URL}/api/hope-forms`;

// Severity Scale Component
function SeverityScale({ value, onChange, label, code, baselineValue, triggersAlert }) {
  const options = [
    { value: "0", label: "Not at all", color: "bg-green-500" },
    { value: "1", label: "Slight", color: "bg-yellow-400" },
    { value: "2", label: "Moderate", color: "bg-orange-500" },
    { value: "3", label: "Severe", color: "bg-red-500" },
    { value: "9", label: "N/A", color: "bg-slate-500" },
  ];

  const showDelta = baselineValue !== undefined && baselineValue !== value;
  const isImproved = baselineValue && value && parseInt(value) < parseInt(baselineValue);
  const isWorsened = baselineValue && value && parseInt(value) > parseInt(baselineValue);
  const triggersSFV = triggersAlert && (value === "2" || value === "3");

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm flex items-center gap-2">
          <span className="text-slate-400 text-xs">{code}</span>
          {label}
          {triggersSFV && (
            <Badge className="bg-red-500/20 text-red-300 text-xs">SFV Required</Badge>
          )}
        </Label>
        {showDelta && (
          <div className="flex items-center gap-1">
            {isImproved && <ArrowDown className="w-4 h-4 text-green-400" />}
            {isWorsened && <ArrowUp className="w-4 h-4 text-red-400" />}
            <span className={`text-xs ${isImproved ? "text-green-400" : isWorsened ? "text-red-400" : "text-slate-400"}`}>
              Baseline: {baselineValue}
            </span>
          </div>
        )}
      </div>
      <div className="flex gap-1">
        {options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`flex-1 py-2 px-1 text-xs rounded transition-all ${
              value === opt.value
                ? `${opt.color} text-white font-medium`
                : "bg-slate-800 text-slate-400 hover:bg-slate-700"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Compliance Alert Banner
function ComplianceAlerts({ alerts }) {
  if (!alerts || alerts.length === 0) return null;

  const criticalAlerts = alerts.filter(a => a.urgency === "VIOLATION" || a.urgency === "OVERDUE");
  const warningAlerts = alerts.filter(a => a.urgency === "WARNING" || a.urgency === "URGENT");
  const infoAlerts = alerts.filter(a => !a.urgency || a.urgency === "INFO");

  return (
    <div className="space-y-2">
      {criticalAlerts.map((alert, idx) => (
        <Alert key={`critical-${idx}`} className="bg-red-500/10 border-red-500/30">
          <XCircle className="h-4 w-4 text-red-400" />
          <AlertTitle className="text-red-300">Compliance Violation</AlertTitle>
          <AlertDescription className="text-red-200">{alert.message}</AlertDescription>
        </Alert>
      ))}
      {warningAlerts.map((alert, idx) => (
        <Alert key={`warning-${idx}`} className="bg-amber-500/10 border-amber-500/30">
          <AlertTriangle className="h-4 w-4 text-amber-400" />
          <AlertTitle className="text-amber-300">Action Required</AlertTitle>
          <AlertDescription className="text-amber-200">{alert.message}</AlertDescription>
        </Alert>
      ))}
      {infoAlerts.map((alert, idx) => (
        <Alert key={`info-${idx}`} className="bg-blue-500/10 border-blue-500/30">
          <Info className="h-4 w-4 text-blue-400" />
          <AlertDescription className="text-blue-200">{alert.message}</AlertDescription>
        </Alert>
      ))}
    </div>
  );
}

// Delta Comparison Display
function DeltaComparison({ delta }) {
  if (!delta) return null;

  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-teal-400" />
          Change from Admission Baseline
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {delta.improvements?.length > 0 && (
          <div>
            <Label className="text-xs text-green-400 flex items-center gap-1">
              <ArrowDown className="w-3 h-3" /> Improvements
            </Label>
            <div className="mt-1 space-y-1">
              {delta.improvements.map((item, idx) => (
                <div key={idx} className="text-xs text-slate-300 flex items-center justify-between">
                  <span>{item.description}</span>
                  <Badge variant="outline" className="text-green-400 border-green-500/30">
                    {item.from} → {item.to}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
        {delta.declines?.length > 0 && (
          <div>
            <Label className="text-xs text-red-400 flex items-center gap-1">
              <ArrowUp className="w-3 h-3" /> Declines
            </Label>
            <div className="mt-1 space-y-1">
              {delta.declines.map((item, idx) => (
                <div key={idx} className="text-xs text-slate-300 flex items-center justify-between">
                  <span>{item.description}</span>
                  <Badge variant="outline" className="text-red-400 border-red-500/30">
                    {item.from} → {item.to}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
        {delta.unchanged?.length > 0 && (
          <div>
            <Label className="text-xs text-slate-400 flex items-center gap-1">
              <Minus className="w-3 h-3" /> Unchanged ({delta.unchanged.length})
            </Label>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Main HOPE Form Component
export function HOPEForm({ 
  patientId, 
  patientName,
  recordType = "admission", 
  existingRecord = null,
  baselineRecord = null,
  onSave,
  onClose 
}) {
  const [formData, setFormData] = useState({
    admin_info: {
      type_of_record: recordType === "admission" ? "1" : recordType === "discharge" ? "9" : "2",
      site_of_service: "",
      admission_date: new Date().toISOString().split("T")[0],
      reason_for_record: recordType === "admission" ? "01" : recordType === "discharge" ? "09" : "02"
    },
    demographics: {
      first_name: "",
      middle_initial: "",
      last_name: "",
      zip_code: "",
      medicare_number: "",
      sex: "",
      birth_date: "",
      ethnicity: "",
      race: [],
      language: "en",
      payer_info: [],
      admitted_from: ""
    },
    diagnoses: {
      principal_diagnosis: "",
      principal_diagnosis_description: "",
      heart_failure: false,
      copd: false
    },
    preferences: {
      cpr_preference_discussed: false,
      life_sustaining_discussed: false,
      hospitalization_discussed: false,
      spiritual_concerns_discussed: false
    },
    health_conditions: {
      death_imminent: false,
      pain: {
        screening_conducted: false,
        pain_severity: "",
        pain_active_problem: false
      },
      shortness_of_breath: {
        screening_conducted: false,
        treatment_initiated: false
      },
      symptom_impact: {
        screening_conducted: false,
        pain_impact: "",
        shortness_of_breath_impact: "",
        anxiety_impact: "",
        nausea_impact: "",
        vomiting_impact: "",
        diarrhea_impact: "",
        constipation_impact: "",
        agitation_impact: ""
      }
    },
    medications: {
      scheduled_opioid_initiated: false,
      prn_opioid_initiated: false,
      bowel_regimen_initiated: false
    }
  });

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("admin");
  const [sfvTriggered, setSfvTriggered] = useState([]);
  const [complianceAlerts, setComplianceAlerts] = useState([]);
  const [delta, setDelta] = useState(null);
  const [isLocked, setIsLocked] = useState(false);

  // Load existing record or baseline
  useEffect(() => {
    if (existingRecord) {
      // Deep merge existing record with default form structure to ensure all fields exist
      const mergedData = {
        admin_info: {
          type_of_record: recordType === "admission" ? "1" : recordType === "discharge" ? "9" : "2",
          site_of_service: "",
          admission_date: new Date().toISOString().split("T")[0],
          reason_for_record: recordType === "admission" ? "01" : recordType === "discharge" ? "09" : "02",
          ...(existingRecord.admin_info || {})
        },
        demographics: {
          first_name: "",
          middle_initial: "",
          last_name: "",
          zip_code: "",
          medicare_number: "",
          sex: "",
          birth_date: "",
          ethnicity: "",
          race: [],
          language: "en",
          payer_info: [],
          admitted_from: "",
          ...(existingRecord.demographics || {})
        },
        diagnoses: {
          principal_diagnosis: "",
          principal_diagnosis_description: "",
          heart_failure: false,
          copd: false,
          ...(existingRecord.diagnoses || {})
        },
        preferences: {
          cpr_preference_discussed: false,
          life_sustaining_discussed: false,
          hospitalization_discussed: false,
          spiritual_concerns_discussed: false,
          ...(existingRecord.preferences || {})
        },
        health_conditions: {
          death_imminent: existingRecord.health_conditions?.death_imminent || false,
          pain: {
            screening_conducted: false,
            pain_severity: "",
            pain_active_problem: false,
            ...(existingRecord.health_conditions?.pain || {})
          },
          shortness_of_breath: {
            screening_conducted: false,
            treatment_initiated: false,
            ...(existingRecord.health_conditions?.shortness_of_breath || {})
          },
          symptom_impact: {
            screening_conducted: false,
            pain_impact: "",
            shortness_of_breath_impact: "",
            anxiety_impact: "",
            nausea_impact: "",
            vomiting_impact: "",
            diarrhea_impact: "",
            constipation_impact: "",
            agitation_impact: "",
            ...(existingRecord.health_conditions?.symptom_impact || {})
          }
        },
        medications: {
          scheduled_opioid_initiated: false,
          prn_opioid_initiated: false,
          bowel_regimen_initiated: false,
          ...(existingRecord.medications || {})
        },
        // Preserve template metadata
        template_applied: existingRecord.template_applied,
        template_name: existingRecord.template_name,
        care_plan_suggestions: existingRecord.care_plan_suggestions,
        sfv_required: existingRecord.sfv_required,
        sfv_deadline: existingRecord.sfv_deadline,
        status: existingRecord.status,
        id: existingRecord.id
      };
      setFormData(mergedData);
      setIsLocked(existingRecord.status === "locked");
      if (existingRecord.delta_changes) {
        setDelta(existingRecord.delta_changes);
      }
    }
    
    // Fetch compliance alerts
    fetchComplianceAlerts();
  }, [existingRecord, patientId, recordType]);

  const fetchComplianceAlerts = async () => {
    try {
      const response = await axios.get(`${HOPE_API}/compliance/alerts/${patientId}`);
      setComplianceAlerts(response.data.alerts || []);
    } catch (err) {
      console.log("Could not fetch compliance alerts");
    }
  };

  // Check for SFV triggers when symptom impact changes
  useEffect(() => {
    const symptoms = formData.health_conditions.symptom_impact;
    const triggered = [];
    
    const checkSymptom = (value, name) => {
      if (value === "2" || value === "3") {
        triggered.push(name);
      }
    };
    
    checkSymptom(symptoms.pain_impact, "Pain");
    checkSymptom(symptoms.shortness_of_breath_impact, "Shortness of Breath");
    checkSymptom(symptoms.anxiety_impact, "Anxiety");
    checkSymptom(symptoms.nausea_impact, "Nausea");
    checkSymptom(symptoms.vomiting_impact, "Vomiting");
    checkSymptom(symptoms.diarrhea_impact, "Diarrhea");
    checkSymptom(symptoms.constipation_impact, "Constipation");
    checkSymptom(symptoms.agitation_impact, "Agitation");
    
    setSfvTriggered(triggered);
  }, [formData.health_conditions.symptom_impact]);

  const updateField = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updateNestedField = (section, subsection, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: {
          ...prev[section][subsection],
          [field]: value
        }
      }
    }));
  };

  const handleSave = async (markComplete = false) => {
    setLoading(true);
    try {
      const payload = {
        patient_id: patientId,
        record_type: recordType,
        ...formData
      };

      let response;
      if (existingRecord?.id) {
        response = await axios.put(`${HOPE_API}/records/${existingRecord.id}`, payload);
      } else {
        response = await axios.post(`${HOPE_API}/records`, payload);
      }

      if (response.data.sfv_required) {
        toast.warning("Symptom Focused Visit Required", {
          description: `Complete SFV within 2 days for: ${sfvTriggered.join(", ")}`
        });
      }

      toast.success(markComplete ? "Assessment completed" : "Draft saved");
      
      if (onSave) onSave(response.data.record);
    } catch (err) {
      toast.error("Failed to save assessment");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLock = async () => {
    if (!existingRecord?.id) {
      toast.error("Save the record first before locking");
      return;
    }

    try {
      await axios.post(`${HOPE_API}/records/${existingRecord.id}/lock`);
      setIsLocked(true);
      toast.success("Record locked for iQIES compliance");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to lock record");
    }
  };

  const getRecordTypeLabel = () => {
    switch (recordType) {
      case "admission": return "HOPE Admission Assessment";
      case "update_1": return "HOPE Update Visit 1 (30-Day)";
      case "update_2": return "HOPE Update Visit 2 (60-Day)";
      case "discharge": return "HOPE Discharge Assessment";
      case "symptom_focused_visit": return "Symptom Focused Visit (SFV)";
      default: return "HOPE Assessment";
    }
  };

  const getEstimatedTime = () => {
    switch (recordType) {
      case "admission": return "41 min";
      case "update_1": 
      case "update_2": return "22 min";
      case "discharge": return "9 min";
      case "symptom_focused_visit": return "15 min";
      default: return "";
    }
  };

  return (
    <div className="space-y-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardList className="w-6 h-6 text-teal-400" />
          <div>
            <h2 className="font-semibold text-lg">{getRecordTypeLabel()}</h2>
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <User className="w-4 h-4" />
              {patientName || patientId}
              <span>•</span>
              <Clock className="w-4 h-4" />
              Est. {getEstimatedTime()}
              {isLocked && (
                <>
                  <span>•</span>
                  <Lock className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-400">Locked</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isLocked && (
            <>
              <Button variant="outline" onClick={() => handleSave(false)} disabled={loading}>
                <Save className="w-4 h-4 mr-1" />
                Save Draft
              </Button>
              <Button onClick={() => handleSave(true)} disabled={loading}>
                <Check className="w-4 h-4 mr-1" />
                Complete
              </Button>
            </>
          )}
          {existingRecord?.id && !isLocked && (
            <Button variant="ghost" onClick={handleLock}>
              <Lock className="w-4 h-4 mr-1" />
              Lock
            </Button>
          )}
        </div>
      </div>

      {/* Compliance Alerts */}
      <ComplianceAlerts alerts={complianceAlerts} />

      {/* SFV Alert Banner */}
      {sfvTriggered.length > 0 && (
        <Alert className="bg-red-500/10 border-red-500/30">
          <AlertTriangle className="h-4 w-4 text-red-400" />
          <AlertTitle className="text-red-300">Symptom Focused Visit Required</AlertTitle>
          <AlertDescription className="text-red-200">
            Moderate/Severe symptoms detected: <strong>{sfvTriggered.join(", ")}</strong>.
            <br />
            SFV must be completed within <strong>2 calendar days</strong>.
          </AlertDescription>
        </Alert>
      )}

      {/* Delta Comparison (for Update visits) */}
      {(recordType === "update_1" || recordType === "update_2") && delta && (
        <DeltaComparison delta={delta} />
      )}

      {/* Form Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full bg-slate-800">
          <TabsTrigger value="admin" className="text-xs">Admin</TabsTrigger>
          <TabsTrigger value="demographics" className="text-xs">Demographics</TabsTrigger>
          <TabsTrigger value="diagnoses" className="text-xs">Diagnoses</TabsTrigger>
          <TabsTrigger value="health" className="text-xs">Health</TabsTrigger>
          <TabsTrigger value="symptoms" className="text-xs">Symptoms</TabsTrigger>
          <TabsTrigger value="medications" className="text-xs">Meds</TabsTrigger>
        </TabsList>

        {/* Section A: Administrative */}
        <TabsContent value="admin" className="space-y-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-sm">Section A: Administrative Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-slate-400">A0050 - Type of Record</Label>
                  <Select 
                    value={formData.admin_info.type_of_record}
                    onValueChange={(v) => updateField("admin_info", "type_of_record", v)}
                    disabled={isLocked}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 - Admission</SelectItem>
                      <SelectItem value="2">2 - Update</SelectItem>
                      <SelectItem value="9">9 - Discharge</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-400">A0215 - Site of Service</Label>
                  <Select 
                    value={formData.admin_info.site_of_service}
                    onValueChange={(v) => updateField("admin_info", "site_of_service", v)}
                    disabled={isLocked}
                  >
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 - Patient's home</SelectItem>
                      <SelectItem value="2">2 - Assisted living facility</SelectItem>
                      <SelectItem value="3">3 - Nursing facility</SelectItem>
                      <SelectItem value="4">4 - Inpatient hospice unit</SelectItem>
                      <SelectItem value="5">5 - Long-term care hospital</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-slate-400">A0220 - Admission Date</Label>
                  <Input 
                    type="date" 
                    value={formData.admin_info.admission_date}
                    onChange={(e) => updateField("admin_info", "admission_date", e.target.value)}
                    disabled={isLocked}
                  />
                </div>
                {recordType === "discharge" && (
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-400">A0270 - Discharge Date</Label>
                    <Input 
                      type="date"
                      onChange={(e) => updateField("admin_info", "discharge_date", e.target.value)}
                      disabled={isLocked}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Demographics Tab */}
        <TabsContent value="demographics" className="space-y-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-sm">Patient Demographics (Golden Record)</CardTitle>
              <CardDescription className="text-xs">
                Changes here may require Master Patient Record update
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-slate-400">A0500A - First Name *</Label>
                  <Input 
                    value={formData.demographics.first_name}
                    onChange={(e) => updateField("demographics", "first_name", e.target.value)}
                    disabled={isLocked}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-400">A0500B - Middle Initial</Label>
                  <Input 
                    value={formData.demographics.middle_initial}
                    onChange={(e) => updateField("demographics", "middle_initial", e.target.value)}
                    maxLength={1}
                    disabled={isLocked}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-400">A0500C - Last Name *</Label>
                  <Input 
                    value={formData.demographics.last_name}
                    onChange={(e) => updateField("demographics", "last_name", e.target.value)}
                    disabled={isLocked}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-slate-400">A0600B - Medicare Number</Label>
                  <Input 
                    value={formData.demographics.medicare_number}
                    onChange={(e) => updateField("demographics", "medicare_number", e.target.value)}
                    disabled={isLocked}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-400">A0810 - Sex *</Label>
                  <Select 
                    value={formData.demographics.sex}
                    onValueChange={(v) => updateField("demographics", "sex", v)}
                    disabled={isLocked}
                  >
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 - Male</SelectItem>
                      <SelectItem value="2">2 - Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-400">A0900 - Birth Date *</Label>
                  <Input 
                    type="date"
                    value={formData.demographics.birth_date}
                    onChange={(e) => updateField("demographics", "birth_date", e.target.value)}
                    disabled={isLocked}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-slate-400">A0550 - ZIP Code</Label>
                  <Input 
                    value={formData.demographics.zip_code}
                    onChange={(e) => updateField("demographics", "zip_code", e.target.value)}
                    maxLength={5}
                    disabled={isLocked}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-400">A1805 - Admitted From</Label>
                  <Select 
                    value={formData.demographics.admitted_from}
                    onValueChange={(v) => updateField("demographics", "admitted_from", v)}
                    disabled={isLocked}
                  >
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="01">01 - Community (private residence)</SelectItem>
                      <SelectItem value="02">02 - Assisted living</SelectItem>
                      <SelectItem value="03">03 - Acute care hospital</SelectItem>
                      <SelectItem value="04">04 - Nursing facility</SelectItem>
                      <SelectItem value="05">05 - Another hospice</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Diagnoses Tab */}
        <TabsContent value="diagnoses" className="space-y-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-sm">Section I: Active Diagnoses</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs text-slate-400">I0010 - Principal Hospice Diagnosis (ICD-10) *</Label>
                <div className="flex gap-2">
                  <Input 
                    value={formData.diagnoses.principal_diagnosis}
                    onChange={(e) => updateField("diagnoses", "principal_diagnosis", e.target.value.toUpperCase())}
                    placeholder="e.g., C34.90"
                    className="w-32"
                    disabled={isLocked}
                  />
                  <Input 
                    value={formData.diagnoses.principal_diagnosis_description}
                    onChange={(e) => updateField("diagnoses", "principal_diagnosis_description", e.target.value)}
                    placeholder="Description"
                    className="flex-1"
                    disabled={isLocked}
                  />
                </div>
              </div>
              
              <Separator />
              
              <div>
                <Label className="text-sm mb-2 block">Comorbidities</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={formData.diagnoses.heart_failure}
                      onCheckedChange={(c) => updateField("diagnoses", "heart_failure", c)}
                      disabled={isLocked}
                    />
                    <Label className="text-sm">I0200 - Heart Failure</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={formData.diagnoses.copd}
                      onCheckedChange={(c) => updateField("diagnoses", "copd", c)}
                      disabled={isLocked}
                    />
                    <Label className="text-sm">I0500 - COPD</Label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Health Conditions Tab */}
        <TabsContent value="health" className="space-y-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-sm">Section J: Health Conditions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-slate-800 rounded-lg">
                <Switch 
                  checked={formData.health_conditions.death_imminent}
                  onCheckedChange={(c) => updateField("health_conditions", "death_imminent", c)}
                  disabled={isLocked}
                />
                <div>
                  <Label className="text-sm">J0050 - Death is Imminent</Label>
                  <p className="text-xs text-slate-400">Within 1-14 days</p>
                </div>
              </div>

              <Separator />

              {/* Pain Assessment */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Pain Assessment</Label>
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={formData.health_conditions.pain.screening_conducted}
                    onCheckedChange={(c) => updateNestedField("health_conditions", "pain", "screening_conducted", c)}
                    disabled={isLocked}
                  />
                  <Label className="text-sm">J0900A - Pain Screening Conducted</Label>
                </div>
                
                {formData.health_conditions.pain.screening_conducted && (
                  <div className="ml-6 space-y-3">
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-400">J0900C - Pain Severity</Label>
                      <Select 
                        value={formData.health_conditions.pain.pain_severity}
                        onValueChange={(v) => updateNestedField("health_conditions", "pain", "pain_severity", v)}
                        disabled={isLocked}
                      >
                        <SelectTrigger><SelectValue placeholder="Rate pain..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">0 - None</SelectItem>
                          <SelectItem value="1">1 - Mild</SelectItem>
                          <SelectItem value="2">2 - Moderate ⚠️</SelectItem>
                          <SelectItem value="3">3 - Severe ⚠️</SelectItem>
                          <SelectItem value="9">9 - Not rated</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={formData.health_conditions.pain.pain_active_problem}
                        onCheckedChange={(c) => updateNestedField("health_conditions", "pain", "pain_active_problem", c)}
                        disabled={isLocked}
                      />
                      <Label className="text-sm">J0905 - Pain Active Problem</Label>
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              {/* Shortness of Breath */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Shortness of Breath Assessment</Label>
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={formData.health_conditions.shortness_of_breath.screening_conducted}
                    onCheckedChange={(c) => updateNestedField("health_conditions", "shortness_of_breath", "screening_conducted", c)}
                    disabled={isLocked}
                  />
                  <Label className="text-sm">J2030A - SOB Screening Conducted</Label>
                </div>
                
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={formData.health_conditions.shortness_of_breath.treatment_initiated}
                    onCheckedChange={(c) => updateNestedField("health_conditions", "shortness_of_breath", "treatment_initiated", c)}
                    disabled={isLocked}
                  />
                  <Label className="text-sm">J2040A - Treatment Initiated</Label>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Symptom Impact Tab */}
        <TabsContent value="symptoms" className="space-y-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-sm">J2051 - Symptom Impact Assessment</CardTitle>
              <CardDescription className="text-xs">
                Moderate (2) or Severe (3) triggers Symptom Focused Visit within 2 days
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Switch 
                  checked={formData.health_conditions.symptom_impact.screening_conducted}
                  onCheckedChange={(c) => updateNestedField("health_conditions", "symptom_impact", "screening_conducted", c)}
                  disabled={isLocked}
                />
                <Label className="text-sm">J2050A - Symptom Impact Screening Conducted</Label>
              </div>

              {formData.health_conditions.symptom_impact.screening_conducted && (
                <div className="space-y-4">
                  <SeverityScale
                    code="J2051A"
                    label="Pain"
                    value={formData.health_conditions.symptom_impact.pain_impact}
                    onChange={(v) => updateNestedField("health_conditions", "symptom_impact", "pain_impact", v)}
                    baselineValue={baselineRecord?.health_conditions?.symptom_impact?.pain_impact}
                    triggersAlert={true}
                  />
                  <SeverityScale
                    code="J2051B"
                    label="Shortness of Breath"
                    value={formData.health_conditions.symptom_impact.shortness_of_breath_impact}
                    onChange={(v) => updateNestedField("health_conditions", "symptom_impact", "shortness_of_breath_impact", v)}
                    baselineValue={baselineRecord?.health_conditions?.symptom_impact?.shortness_of_breath_impact}
                    triggersAlert={true}
                  />
                  <SeverityScale
                    code="J2051C"
                    label="Anxiety"
                    value={formData.health_conditions.symptom_impact.anxiety_impact}
                    onChange={(v) => updateNestedField("health_conditions", "symptom_impact", "anxiety_impact", v)}
                    baselineValue={baselineRecord?.health_conditions?.symptom_impact?.anxiety_impact}
                    triggersAlert={true}
                  />
                  <SeverityScale
                    code="J2051D"
                    label="Nausea"
                    value={formData.health_conditions.symptom_impact.nausea_impact}
                    onChange={(v) => updateNestedField("health_conditions", "symptom_impact", "nausea_impact", v)}
                    baselineValue={baselineRecord?.health_conditions?.symptom_impact?.nausea_impact}
                    triggersAlert={true}
                  />
                  <SeverityScale
                    code="J2051E"
                    label="Vomiting"
                    value={formData.health_conditions.symptom_impact.vomiting_impact}
                    onChange={(v) => updateNestedField("health_conditions", "symptom_impact", "vomiting_impact", v)}
                    baselineValue={baselineRecord?.health_conditions?.symptom_impact?.vomiting_impact}
                    triggersAlert={true}
                  />
                  <SeverityScale
                    code="J2051F"
                    label="Diarrhea"
                    value={formData.health_conditions.symptom_impact.diarrhea_impact}
                    onChange={(v) => updateNestedField("health_conditions", "symptom_impact", "diarrhea_impact", v)}
                    baselineValue={baselineRecord?.health_conditions?.symptom_impact?.diarrhea_impact}
                    triggersAlert={true}
                  />
                  <SeverityScale
                    code="J2051G"
                    label="Constipation"
                    value={formData.health_conditions.symptom_impact.constipation_impact}
                    onChange={(v) => updateNestedField("health_conditions", "symptom_impact", "constipation_impact", v)}
                    baselineValue={baselineRecord?.health_conditions?.symptom_impact?.constipation_impact}
                    triggersAlert={true}
                  />
                  <SeverityScale
                    code="J2051H"
                    label="Agitation"
                    value={formData.health_conditions.symptom_impact.agitation_impact}
                    onChange={(v) => updateNestedField("health_conditions", "symptom_impact", "agitation_impact", v)}
                    baselineValue={baselineRecord?.health_conditions?.symptom_impact?.agitation_impact}
                    triggersAlert={true}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Medications Tab */}
        <TabsContent value="medications" className="space-y-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-sm">Section N: Medications</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                <div>
                  <Label className="text-sm">N0500 - Scheduled Opioid</Label>
                  <p className="text-xs text-slate-400">Initiated or continued during this visit</p>
                </div>
                <Switch 
                  checked={formData.medications.scheduled_opioid_initiated}
                  onCheckedChange={(c) => updateField("medications", "scheduled_opioid_initiated", c)}
                  disabled={isLocked}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                <div>
                  <Label className="text-sm">N0510 - PRN Opioid</Label>
                  <p className="text-xs text-slate-400">Initiated or continued during this visit</p>
                </div>
                <Switch 
                  checked={formData.medications.prn_opioid_initiated}
                  onCheckedChange={(c) => updateField("medications", "prn_opioid_initiated", c)}
                  disabled={isLocked}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                <div>
                  <Label className="text-sm">N0520 - Bowel Regimen</Label>
                  <p className="text-xs text-slate-400">Initiated or continued during this visit</p>
                </div>
                <Switch 
                  checked={formData.medications.bowel_regimen_initiated}
                  onCheckedChange={(c) => updateField("medications", "bowel_regimen_initiated", c)}
                  disabled={isLocked}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Footer Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-slate-700">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <div className="flex items-center gap-2">
          {!isLocked && (
            <>
              <Button variant="outline" onClick={() => handleSave(false)} disabled={loading}>
                <Save className="w-4 h-4 mr-1" />
                Save Draft
              </Button>
              <Button onClick={() => handleSave(true)} disabled={loading} className="bg-teal-600 hover:bg-teal-700">
                <CheckCircle className="w-4 h-4 mr-1" />
                Complete Assessment
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// HOPE Records List Component
export function HOPERecordsList({ patientId, patientName, onSelectRecord, onNewRecord }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    fetchRecords();
    fetchAlerts();
  }, [patientId]);

  const fetchRecords = async () => {
    try {
      const response = await axios.get(`${HOPE_API}/records/${patientId}`);
      setRecords(response.data.records || []);
    } catch (err) {
      console.log("Could not fetch HOPE records");
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${HOPE_API}/compliance/alerts/${patientId}`);
      setAlerts(response.data.alerts || []);
    } catch (err) {
      console.log("Could not fetch alerts");
    }
  };

  const getRecordIcon = (type) => {
    switch (type) {
      case "admission": return <FileText className="w-4 h-4 text-green-400" />;
      case "update_1":
      case "update_2": return <RefreshCw className="w-4 h-4 text-blue-400" />;
      case "discharge": return <Check className="w-4 h-4 text-amber-400" />;
      case "symptom_focused_visit": return <Activity className="w-4 h-4 text-red-400" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "draft": return <Badge variant="outline">Draft</Badge>;
      case "in_progress": return <Badge className="bg-blue-500/20 text-blue-300">In Progress</Badge>;
      case "completed": return <Badge className="bg-green-500/20 text-green-300">Completed</Badge>;
      case "locked": return <Badge className="bg-amber-500/20 text-amber-300"><Lock className="w-3 h-3 mr-1" />Locked</Badge>;
      default: return null;
    }
  };

  const hasAdmission = records.some(r => r.record_type === "admission");

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-teal-400" />
          <h3 className="font-semibold">HOPE Assessments</h3>
          <Badge variant="outline">{patientName}</Badge>
        </div>
        <Select onValueChange={onNewRecord}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="New Assessment..." />
          </SelectTrigger>
          <SelectContent>
            {!hasAdmission && <SelectItem value="admission">HOPE Admission</SelectItem>}
            <SelectItem value="update_1">Update Visit 1</SelectItem>
            <SelectItem value="update_2">Update Visit 2</SelectItem>
            <SelectItem value="symptom_focused_visit">Symptom Focused Visit</SelectItem>
            <SelectItem value="discharge">Discharge</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Compliance Alerts */}
      <ComplianceAlerts alerts={alerts} />

      {/* Records List */}
      <ScrollArea className="h-[400px]">
        {loading ? (
          <div className="text-center py-8 text-slate-400">Loading...</div>
        ) : records.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="w-12 h-12 mx-auto mb-3 text-slate-600" />
            <p className="text-slate-400">No HOPE assessments yet</p>
            <p className="text-xs text-slate-500 mt-1">Start with an Admission assessment</p>
          </div>
        ) : (
          <div className="space-y-2">
            {records.map((record) => (
              <div
                key={record.id}
                className="p-3 bg-slate-800 rounded-lg border border-slate-700 hover:border-slate-600 cursor-pointer transition-colors"
                onClick={() => onSelectRecord(record)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getRecordIcon(record.record_type)}
                    <div>
                      <p className="font-medium text-sm">
                        {record.record_type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                      </p>
                      <p className="text-xs text-slate-400">
                        {new Date(record.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(record.status)}
                    {record.sfv_required && (
                      <Badge className="bg-red-500/20 text-red-300">SFV Due</Badge>
                    )}
                    <ChevronRight className="w-4 h-4 text-slate-500" />
                  </div>
                </div>
                {record.delta_changes && (record.delta_changes.improvements?.length > 0 || record.delta_changes.declines?.length > 0) && (
                  <div className="mt-2 flex items-center gap-3 text-xs">
                    {record.delta_changes.improvements?.length > 0 && (
                      <span className="text-green-400 flex items-center gap-1">
                        <ArrowDown className="w-3 h-3" />
                        {record.delta_changes.improvements.length} improved
                      </span>
                    )}
                    {record.delta_changes.declines?.length > 0 && (
                      <span className="text-red-400 flex items-center gap-1">
                        <ArrowUp className="w-3 h-3" />
                        {record.delta_changes.declines.length} declined
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

// iQIES Export Component
export function IQIESExport({ patientIds, onExport }) {
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${HOPE_API}/export/batch`, {
        params: { patient_ids: patientIds?.join(",") }
      });
      
      setResult(response.data);
      
      if (response.data.success) {
        toast.success(`Exported ${response.data.record_count} records`);
        if (onExport) onExport(response.data);
      } else {
        toast.error("Export validation failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const downloadXML = () => {
    if (!result?.xml_content) return;
    
    const blob = new Blob([result.xml_content], { type: "application/xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hope_batch_${new Date().toISOString().split("T")[0]}.xml`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <Download className="w-4 h-4 text-teal-400" />
          iQIES Batch Export
        </CardTitle>
        <CardDescription className="text-xs">
          Export locked HOPE records for CMS submission
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button onClick={handleExport} disabled={exporting} className="w-full">
          {exporting ? "Validating..." : "Run Pre-flight & Export"}
        </Button>
        
        {result && (
          <div className="space-y-2">
            {result.success ? (
              <>
                <Alert className="bg-green-500/10 border-green-500/30">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  <AlertTitle className="text-green-300">Export Ready</AlertTitle>
                  <AlertDescription className="text-green-200">
                    {result.record_count} records validated successfully
                  </AlertDescription>
                </Alert>
                <Button onClick={downloadXML} variant="outline" className="w-full">
                  <Download className="w-4 h-4 mr-2" />
                  Download XML File
                </Button>
              </>
            ) : (
              <Alert className="bg-red-500/10 border-red-500/30">
                <XCircle className="h-4 w-4 text-red-400" />
                <AlertTitle className="text-red-300">Validation Failed</AlertTitle>
                <AlertDescription className="text-red-200">
                  {result.errors?.map((err, idx) => (
                    <div key={idx} className="text-xs mt-1">
                      Record {err.record_id}: {err.errors.join(", ")}
                    </div>
                  ))}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default HOPEForm;
